import { createEffect, createSignal, For, type JSX } from "solid-js";

import { render } from "solid-js/web";
import { game } from "~/js/game";
import { removeItemsFromInventory, showSelectItems } from "~/js/items";
import { Pages } from "~/js/pages";
import { getCurrency } from "~/js/prefs";
import { addStat } from "~/js/stats";
import { createPage } from "~/js/utils";
import { goToPreviousPage, numToValuta, showPage } from "~/script";
import { user } from "~/userStore";

const CHIPS_PER_100_CASH = 8 as const;

function chipsToCash(chips: number) {
	return (chips / CHIPS_PER_100_CASH) * 100;
}

function WithdrawPageSolid() {
	const [amount, setAmount] = createSignal(String(Math.floor(user.tokens)));
	const [buttonLabel, setButtonLabel] = createSignal("Withdraw");
	const [disabled, setDisabled] = createSignal(true);

	createEffect(() => {
		const val = Math.abs(parseInt(amount(), 10) || 0);
		const valid = val > 0 && val <= user.tokens;
		setDisabled(!valid);
		if (valid) setButtonLabel(`Withdraw ${numToValuta(chipsToCash(val), 2)}`);
		else setButtonLabel("Withdraw");
	});

	function doWithdraw() {
		const val = Math.abs(parseInt(amount(), 10) || 0);
		if (!(val > 0 && val <= user.tokens)) return;
		const cashValue = chipsToCash(val);
		user.money += cashValue;
		addStat("withdrawn_from_chips", cashValue);
		user.tokens -= val;
		user.tokens = Math.round(user.tokens);
		game.emit("update");
		setAmount(String(Math.floor(user.tokens)));
	}

	return (
		<>
			<button class="button back-btn" onClick={goToPreviousPage} type="button">
				Back
			</button>
			<div class="center center-v">
				<h1 style={{ "padding-top": "0px" }}>Casino Withdraw</h1>
				<div class="withdraw-conversion">
					<img alt="Tokens" class="withdraw-c-left" src="images/tokens.png" />
					<img alt="Turn into" class="withdraw-c-center" src="images/arrow-right.png" />
					<span class="withdraw-c-right">{getCurrency()}</span>
				</div>
				<div class="withdraw-description">Withdraw Cash from the Casino, by turning in your chips.</div>
				<div class="withdraw-description">
					{CHIPS_PER_100_CASH} Chips are worth {numToValuta(100, 2)}
				</div>
				<input
					class="input"
					oninput={(e) => setAmount((e.target as HTMLInputElement).value)}
					onkeyup={(e: KeyboardEvent) => e.key === "Enter" && doWithdraw()}
					placeholder="0"
					value={amount()}
				/>
				<button class="button" disabled={disabled()} onClick={doWithdraw} style={{ "margin-top": "10px" }} type="button">
					{buttonLabel()}
				</button>
			</div>
		</>
	);
}

createPage(() => <WithdrawPageSolid />, "casino-withdraw");

export function openWithdrawPrompt() {
	// #TODO: inline this
	showPage("casino-withdraw");
}

export async function tradeIn() {
	const selectedItems = await showSelectItems(0, null, Infinity, 1, false, false, true);
	if (!selectedItems || selectedItems.length === 0) {
		goToPreviousPage();
		return;
	}
	const totalValue = selectedItems.reduce((a, b) => a + b.price, 0);
	addStat("skins_tradein", totalValue);
	addStat("skins_tradein_amount", selectedItems.length);
	user.tokens = Math.round(user.tokens + (totalValue / 100) * CHIPS_PER_100_CASH);
	removeItemsFromInventory(selectedItems.map((it) => it.name));
	game.emit("update");
	goToPreviousPage();
}

// Betting Page
function BettingPage() {
	const offlineGames = [
		{
			image: "images/coin_ctt.png",
			alt: "Coinflip",
			onClick: () => showPage("coinflip"),
			buttonText: "Play Coinflip Offline",
			disabled: false,
		},
		{
			image: "images/roulette.png",
			alt: "Roulette",
			onClick: () => showPage("roulette"),
			buttonText: "Play Roulette",
			disabled: false,
		},
		{
			image: "images/slots.png",
			alt: "Slots",
			onClick: () => showPage("slots"),
			buttonText: "Play Slot Machine",
			disabled: false,
		},
		{
			image: "images/dice.png",
			alt: "Jackpot",
			onClick: () => showPage("jackpot"),
			buttonText: "Play Jackpot",
			disabled: false,
		},
		{
			image: "images/crash.png",
			alt: "Crash",
			onClick: () => showPage("crash"),
			buttonText: "Play Crash",
			disabled: false,
		},
		{
			image: "images/bomb.png",
			alt: "Mines",
			onClick: () => showPage("mines"),
			buttonText: "Play Mines",
			disabled: false,
		},
	] satisfies BettingGameProps[];
	const onlineGames = [
		{
			image: "images/coin_ctt.png",
			alt: "Coinflip",
			onClick: () => showPage("coinflip-online"),
			buttonText: "Play Online Coinflip 1v1",
			disabled: true,
		},
		{
			image: "images/jackpot.png",
			alt: "Coinflip",
			onClick: () => showPage("jackpot-online"),
			buttonText: "Play Online Jackpot",
			disabled: true,
			children: (
				<span
					style={{
						color: "#000",
						"font-weight": "600",
						background: "#fc0",
						padding: "2px 3px",
						"border-radius": "2px",
						"margin-left": "10px",
					}}
				>
					NEW!
				</span>
			),
		},
	] satisfies BettingGameProps[];

	return (
		<div class="center center-v">
			<h1 style={{ "padding-top": "0px" }}>Casino</h1>
			<div style={{ display: "flex", "justify-content": "center", "margin-bottom": "20px" }}>
				<button class="button" on:click={tradeIn} type="button">
					Trade in skins to Chips
				</button>
				<button class="button" on:click={openWithdrawPrompt} type="button">
					Withdraw Casino Chips to {getCurrency()}
				</button>
			</div>
			<div class="betting-cols">
				<div>
					<p style={{ "font-size": "14px", "margin-bottom": "16px", color: "#999", "text-align": "center" }}>GAMES</p>
					<For each={offlineGames}>
						{(game) => (
							<BettingGame alt={game.alt} buttonText={game.buttonText} disabled={game.disabled} image={game.image} onClick={game.onClick} />
						)}
					</For>
				</div>
				<div>
					<p style={{ "font-size": "14px", "margin-bottom": "16px", color: "#999", "text-align": "center" }}>
						ONLINE GAMES (CURRENTLY UNAVAILABLE)
					</p>
					<For each={onlineGames}>
						{(game) => (
							<BettingGame alt={game.alt} buttonText={game.buttonText} disabled={game.disabled} image={game.image} onClick={game.onClick}>
								{game.children}
							</BettingGame>
						)}
					</For>
				</div>
			</div>
		</div>
	);
}
interface BettingGameProps {
	image: string;
	alt: string;
	onClick: JSX.EventHandler<HTMLButtonElement, Event>;
	buttonText: string;
	disabled: boolean;
	children?: JSX.Element;
}
function BettingGame(props: BettingGameProps) {
	return (
		<div
			class="betting-game"
			style={{
				filter: props.disabled ? "grayscale(100%)" : "",
				cursor: props.disabled ? "not-allowed" : "pointer",
			}}
		>
			<img alt={props.alt} src={props.image} />
			<button
				on:click={props.onClick}
				disabled={props.disabled}
				style={{ cursor: props.disabled ? "not-allowed" : "pointer" }}
				type="button"
			>
				{props.buttonText}
				{props.children}
			</button>
		</div>
	);
}

const bettingPage = Pages.createPage("betting");

const bettingContainerSolid = document.createElement("div");
render(() => <BettingPage></BettingPage>, bettingContainerSolid);
while (bettingContainerSolid.firstElementChild) {
	bettingPage.append(bettingContainerSolid.firstElementChild);
}
